import React, { useEffect, useState } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { Label } from '@abyss/web/ui/Label';
import { Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { DataTable } from '@abyss/web/ui/DataTable';
import { useDataTable } from '@abyss/web/hooks/useDataTable';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { API_URL } from '@src/constants/BaseUrls';
import {
  RouteToolEntityOption,
  RouteToolHistoryRecord,
} from '../RouteToolHistoryStore';
import { Breadcrumbs, Title } from './PageInfo';
import { CookieService } from '@src/api/common/CookieService';

const UPLOAD_PROCESSING_KEY = 'routeToolUploadProcessing';

const displayCount = (value: number | null) =>
  value === null || value === undefined ? '-' : String(value);

const displayTime = (value: string | null) => (value ? value : '-');

const hasUploadedInputData = (record: RouteToolHistoryRecord): boolean => {
  const clinicSites = Number(record.clinicSites || 0);
  const warehouses = Number(record.warehouses || 0);
  const remoteUsers = Number(record.remoteUsers || 0);

  return clinicSites > 0 || warehouses > 0 || remoteUsers > 0;
};

const normalizeBaseUrl = (baseUrl?: string) => {
  if (!baseUrl) {
    return '';
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveRouteToolApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) {
    return '';
  }
  return configured;
};

const buildApiUrl = (baseUrl: string, path: string) => {
  if (baseUrl.endsWith('/api') && path.startsWith('/api/')) {
    return `${baseUrl}${path.slice(4)}`;
  }
  return `${baseUrl}${path}`;
};

type EntitySummary = {
  entity?: string;
  last_modified?: string | null;
  total_clinic_sites?: number | null;
  total_warehouses?: number | null;
  total_remote_users?: number | null;
  status?: string;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toUiStatus = (status?: string): string => {
  const normalized = (status || '').toLowerCase();
  if (!normalized) {
    return 'Unknown';
  }

  if (['failed', 'error', 'cancelled'].includes(normalized)) {
    return 'Failed';
  }

  if (['completed', 'succeeded', 'success', 'done'].includes(normalized)) {
    return 'Completed';
  }

  if (
    ['processing', 'running', 'queued', 'pending', 'in_progress'].includes(
      normalized
    )
  ) {
    return 'Processing';
  }

  return status || 'Unknown';
};

const toUploadDate = (value?: string | null): string => {
  if (!value) {
    return new Date().toISOString().slice(0, 10);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }

  return parsed.toISOString().slice(0, 10);
};

const toUploadTime = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const toTimestamp = (value?: string | null): number => {
  if (!value) {
    return 0;
  }

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const SearchScreen = () => {
  const { navigate, getLocation } = useRouter();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const baseUrl = resolveRouteToolApiBase();

  // Returns Authorization header using the token stored by MsalService after login.
  // No credentials mode needed — Bearer token auth does not use browser-managed cookies.
  const getAuthHeaders = (): HeadersInit => {
    const token = CookieService.GetCookie('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [entityOptions, setEntityOptions] = useState<RouteToolEntityOption[]>(
    []
  );
  const [selectedEntity, setSelectedEntity] = useState('');
  const [allRecords, setAllRecords] = useState<RouteToolHistoryRecord[]>([]);
  const [filtered, setFiltered] = useState<RouteToolHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState('');
  const [recordsError, setRecordsError] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');
  const [isEntitySelectFocused, setIsEntitySelectFocused] = useState(false);

  // Upload processing progress bar state
  const [uploadProcessing, setUploadProcessing] = useState<{ entity: string; startedAt: number } | null>(null);
  const [uploadProcessingMsg, setUploadProcessingMsg] = useState('');
  const [uploadBarProgress, setUploadBarProgress] = useState(5);

  const getBackendSummaries = async (): Promise<EntitySummary[]> => {
    let response: Response;
    try {
      response = await fetch(
        buildApiUrl(baseUrl, '/api/entities/summary'),
        {
          headers: getAuthHeaders(),
        }
      );
    } catch {
      // Network error (server unreachable, CORS, etc.) — return empty gracefully
      return [];
    }

    if (!response.ok) {
      return [];
    }

    const payload = await response.json().catch(() => ({}));
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.entities)) {
      return payload.entities;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    if (Array.isArray(payload?.items)) {
      return payload.items;
    }

    return [];
  };

  const toBackendRecord = (
    summary: EntitySummary
  ): RouteToolHistoryRecord | null => {
    const entityName = String(summary?.entity || '').trim();
    if (!entityName) {
      return null;
    }

    return {
      id: `summary-${entityName}`,
      entityName,
      uploadDate: toUploadDate(summary?.last_modified),
      uploadTime: toUploadTime(summary?.last_modified),
      clinicSites: toNullableNumber(summary?.total_clinic_sites),
      warehouses: toNullableNumber(summary?.total_warehouses),
      remoteUsers: toNullableNumber(summary?.total_remote_users),
      status: toUiStatus(summary?.status || 'Completed'),
    };
  };

  useEffect(() => {
    (async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
      await loadEntities();

      // Auto-select entity passed via ?entity= query param (e.g. from Dashboard).
      try {
        const params = new URLSearchParams(getLocation?.()?.search || '');
        const entityParam = decodeURIComponent(params.get('entity') || '').trim();
        if (entityParam) {
          setSelectedEntity(entityParam);
          void loadEntityHistory(entityParam);
        }
      } catch {
        // ignore malformed query param
      }
    })();

    // Check for an in-progress upload started from the Upload page.
    try {
      const raw = window.sessionStorage.getItem(UPLOAD_PROCESSING_KEY);
      if (raw) {
        const state = JSON.parse(raw) as { entity: string; startedAt: number };
        if (state?.entity) {
          setUploadProcessing(state);
          setUploadProcessingMsg('Processing uploaded files…');
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Listen for upload-started events fired by UploadScreen on the same tab
  // (sessionStorage events don't fire within the same tab, so we use a custom event).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ entity: string; startedAt: number }>).detail;
      if (detail?.entity) {
        setUploadProcessing({ entity: detail.entity, startedAt: detail.startedAt });
        setUploadProcessingMsg('Processing uploaded files…');
      }
    };
    window.addEventListener('routeToolUploadStarted', handler);
    return () => window.removeEventListener('routeToolUploadStarted', handler);
  }, []);

  useEffect(() => {
    setFiltered(allRecords);
  }, [allRecords]);

  // Fake progress animation while upload is being processed by the backend.
  useEffect(() => {
    if (!uploadProcessing) {
      return undefined;
    }
    setUploadBarProgress(5);
    const interval = setInterval(() => {
      setUploadBarProgress((prev) => {
        const next = prev + Math.random() * 6 + 2;
        return next >= 90 ? 90 : next;
      });
    }, 700);
    return () => clearInterval(interval);
  }, [uploadProcessing]);

  // Poll backend status while processing and dismiss bar when complete.
  useEffect(() => {
    if (!uploadProcessing) return undefined;
    let cancelled = false;
    const { entity, startedAt } = uploadProcessing;
    const statusUrl = buildApiUrl(baseUrl, `/api/entities/${encodeURIComponent(entity)}/status`);

    const poll = async () => {
      while (!cancelled) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(statusUrl, { headers: getAuthHeaders() });
          if (response.ok) {
            // eslint-disable-next-line no-await-in-loop
            const body = await response.json().catch(() => ({}));
            const blobs = body?.blobs ?? {};
            const allComplete =
              blobs.all_complete === true ||
              (blobs.site_clusters === true &&
                blobs.site_travel === true &&
                blobs.user_sites === true &&
                blobs.warehouse_clusters === true);
            const stage: string = body?.job?.stage || body?.stage || '';
            const elapsed = Math.round((Date.now() - startedAt) / 1000);
            const stageLabel = stage ? ` — ${stage}` : '';
            setUploadProcessingMsg(`Processing${stageLabel} (${elapsed}s)…`);
            if (allComplete) {
              setUploadBarProgress(100);
              setTimeout(() => {
                try { window.sessionStorage.removeItem(UPLOAD_PROCESSING_KEY); } catch { /* */ }
                setUploadProcessing(null);
                setUploadProcessingMsg('');
                void loadEntities();
              }, 1200);
              return;
            }
          }
        } catch {
          // network error — keep polling
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => { setTimeout(resolve, 3000); });
      }
    };

    void poll();
    return () => { cancelled = true; };
  }, [uploadProcessing]);

  useEffect(() => {
    if (!selectedEntity) {
      return;
    }

    const interval = window.setInterval(() => {
      loadEntityHistory(selectedEntity, true);
    }, 30000);

    return () => window.clearInterval(interval);
  }, [selectedEntity]);

  useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      if (event.key && event.key !== 'routeToolHistoryRecords') {
        return;
      }

      loadEntities();
      if (selectedEntity) {
        loadEntityHistory(selectedEntity, true);
      }
    };

    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [selectedEntity]);

  const loadEntities = async () => {
    setEntitiesError('');

    const summaries = await getBackendSummaries();
    const options = summaries
      .filter((item) => String(item?.status || '').toLowerCase() !== 'removed')
      .map((item) => String(item?.entity || '').trim())
      .filter((name) => Boolean(name))
      .map((name) => ({ value: name, label: name })) as RouteToolEntityOption[];

    if (options.length === 0) {
      setEntityOptions([]);
      setAllRecords([]);
      setRecordsError('No backend records found yet.');
      setEntitiesError('No backend entities found yet.');
      return;
    }

    setEntityOptions(options);

    if (selectedEntity && !options.some((option) => option.value === selectedEntity)) {
      setSelectedEntity('');
    }

    if (!selectedEntity) {
      const defaultRecords = summaries
        .slice()
        .sort(
          (a, b) =>
            toTimestamp(b?.last_modified) - toTimestamp(a?.last_modified)
        )
        .map((summary) => toBackendRecord(summary))
        .filter((record): record is RouteToolHistoryRecord => Boolean(record));

      setAllRecords(defaultRecords);
      setRecordsError(
        defaultRecords.length > 0 ? '' : 'No backend records found yet.'
      );
      setLastUpdatedAt(new Date().toLocaleString());
    }
  };

  const loadEntityHistory = async (
    entityName: string,
    isBackgroundRefresh = false
  ) => {
    if (!entityName) {
      setAllRecords([]);
      setRecordsError('');
      setLastUpdatedAt('');
      return;
    }

    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setRecordsError('');

    const summaries = await getBackendSummaries();
    const summary = summaries.find(
      (item) => String(item?.entity || '').trim() === entityName
    );
    const record = summary ? toBackendRecord(summary) : null;
    const records = record ? [record] : [];

    setAllRecords(records);
    setLastUpdatedAt(new Date().toLocaleString());
    if (records.length === 0) {
      setRecordsError('No uploads found for this entity yet.');
    }

    if (!isBackgroundRefresh) {
      setLoading(false);
    }
  };

  const handleEntityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedEntity(value);
    void loadEntityHistory(value);
  };

  const handleReset = () => {
    setSelectedEntity('');
    setAllRecords([]);
    setFiltered([]);
    setRecordsError('');
    setLastUpdatedAt('');
    void loadEntities();
  };

  const tableColumns = [
    {
      Header: 'Entity Name',
      accessor: 'entityName',
      width: 180,
      minWidth: 120,
      Cell: ({ value }: { value: string }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', minWidth: 0 }}>
          <IconSymbol icon="corporate_fare" size={14} color="$primary1" />
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#1a2d4a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </div>
      ),
    },
    {
      Header: 'Upload Date & Time',
      accessor: 'uploadDate',
      width: 150,
      minWidth: 130,
      Cell: ({ row }: { row: { original: RouteToolHistoryRecord } }) => (
        <span style={{ fontSize: '12px', color: '#5a6a7e', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {`${row.original.uploadDate} ${displayTime(row.original.uploadTime)}`}
        </span>
      ),
    },
    {
      Header: 'Clinic Sites',
      accessor: 'clinicSites',
      width: 90,
      minWidth: 80,
      Cell: ({ value }: { value: number | null }) => (
        <span style={{ fontSize: '12px', color: '#3d4f60', fontWeight: 600 }}>
          {displayCount(value)}
        </span>
      ),
    },
    {
      Header: 'Warehouses',
      accessor: 'warehouses',
      width: 90,
      minWidth: 80,
      Cell: ({ value }: { value: number | null }) => (
        <span style={{ fontSize: '12px', color: '#3d4f60', fontWeight: 600 }}>
          {displayCount(value)}
        </span>
      ),
    },
    {
      Header: 'Remote Users',
      accessor: 'remoteUsers',
      width: 100,
      minWidth: 90,
      Cell: ({ value }: { value: number | null }) => (
        <span style={{ fontSize: '12px', color: '#3d4f60', fontWeight: 600 }}>
          {displayCount(value)}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'id',
      width: 80,
      minWidth: 70,
      disableSortBy: true,
      Cell: ({ row }: { row: { original: RouteToolHistoryRecord } }) => {
        const canView = hasUploadedInputData(row.original);

        return (
          <Button
            size="$sm"
            variant="outline"
            disabled={!canView}
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '20px',
              padding: '3px 10px',
              fontSize: '12px',
              fontWeight: 600,
              borderColor: '#196ECF',
              color: '#196ECF',
              '&:hover': { backgroundColor: '#e8f2fc' },
            }}
            onClick={() => {
              if (!canView) return;
              navigate(
                `/route-tool/tables/${encodeURIComponent(row.original.entityName)}`
              );
            }}
          >
            <IconSymbol icon="open_in_new" size={12} />
            View
          </Button>
        );
      },
    },
  ];

  const tableState = useDataTable({
    initialData: filtered,
    initialColumns: tableColumns,
    uniqueStorageId: 'route-tool-search-results',
    showPagination: true,
    showGlobalFilter: false,
    showFilterDataset: false,
    showTableSettings: false,
    showTopPagination: true,
    showBottomPagination: true,
  });

  useEffect(() => {
    tableState.setData(filtered);
  }, [filtered]);

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Card>
        <Card.Section css={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Label
            size="md"
            color="$gray8"
            css={{ marginBottom: '-8px', fontSize: '18px', fontWeight: 700 }}
          >
            Acquired Entity
          </Label>

          <select
            value={selectedEntity}
            onChange={handleEntityChange}
            onFocus={() => setIsEntitySelectFocused(true)}
            onBlur={() => setIsEntitySelectFocused(false)}
            style={{
              width: '100%',
              height: '40px',
              border: `1px solid ${isEntitySelectFocused ? '#196ecf' : '#d3dce8'}`,
              borderRadius: '4px',
              padding: '0 12px',
              backgroundColor: '#fff',
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          >
            <option value="">Select acquired entity</option>
            {entityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {entitiesError ? (
            <Label size="sm" color="$error1">
              {entitiesError}
            </Label>
          ) : null}

          <Layout.Group
            space={10}
            css={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
          >
            <Button
              size="$sm"
              onClick={() => {
                void loadEntityHistory(selectedEntity);
              }}
              disabled={!selectedEntity}
            >
              <IconSymbol icon="refresh" size={16} />
              Refresh
            </Button>
            <Button size="$sm" variant="outline" onClick={handleReset}>
              <IconSymbol icon="restart_alt" size={16} />
              Reset
            </Button>
          </Layout.Group>

          {loading ? (
            <Box css={{ padding: '20px 0' }}>
              <Label size="sm" color="$gray6">
                Loading upload history...
              </Label>
            </Box>
          ) : filtered.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '16px 4px',
              }}
            >
              <IconSymbol icon="search_off" size={18} color="$gray4" />
              <span style={{ fontSize: '13px', color: '#8a96a3', fontWeight: 500 }}>
                {recordsError || 'No records found.'}
              </span>
            </div>
          ) : (
            <Box
              css={{
                '&& table th, && table td': {
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  fontSize: '12px',
                  lineHeight: '1.2',
                },
                '&& table th': { fontWeight: 700 },
                '&& table': { width: '100%', tableLayout: 'fixed' },
              }}
            >
              <DataTable title="" tableState={tableState} hideTitleHeader />
            </Box>
          )}

          {lastUpdatedAt ? (
            <Label size="xs" color="$gray5" css={{ alignSelf: 'flex-end' }}>
              Last updated: {lastUpdatedAt}
            </Label>
          ) : null}
        </Card.Section>
      </Card>
    </Box>
  );
};
